import { useState } from "react";
import styled from "@emotion/styled";
import { GrButton, GrDivider, GrIconButton, GrMenu, GrStack, palette, typography } from "@jnpmedi/grebob";
import { TextLogo } from "@newComponents/common/logo/TextLogo";
import { langHelper } from "@newLibs/utils";
import { UseBoolean } from "@newHooks/common/useBoolean";
import { useLayoutContext } from "@newHooks/context/layout/layout.context";
import baseMedia from "@assets/theme/baseDefine/baseMedia";
import { baseZIndex } from "@assets/theme/baseDefine/baseZIndex";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { baseWidth } from "@assets/theme/baseSize/baseWidth";
import { MvnLanguageText, useHeader } from "./hooks/useHeader";
import { LoLang, readableLoLang } from "@maven-rest/common/common.types";

export function PublicHeader() {
  const language = langHelper.getLang();
  const { sidebarCollapsed, isHiddenSideBar } = useLayoutContext();
  const { handleChangeLocaleLanguage } = useHeader();

  const [languageEl, setLanguageEl] = useState<HTMLElement | null>(null);

  return (
    <HeaderWrapper sidebarCollapsed={sidebarCollapsed} isHiddenSidebar={isHiddenSideBar}>
      <TextLogo />
      <HeaderItemsWrapper>
        <HeaderItemsNavigator flexDirection="row">
          <TextButton
            variant="text"
            startIcon="world"
            endIcon={languageEl ? "chevron-up" : "chevron-down"}
            color="white"
            onClick={(e) => setLanguageEl(e.currentTarget)}
          >
            {readableLoLang(language)}
          </TextButton>
          <Divider />
          <QuestionMarkIconButton
            iconType="question-mark"
            color="white"
            size={16}
            onClick={() => window.open("https://mvn.do/guides/docs")}
          />
        </HeaderItemsNavigator>
        <GrMenu
          anchorElement={languageEl}
          position={{ vertical: "bottom", horizontal: "right" }}
          items={[
            {
              onClick: () => handleChangeLocaleLanguage(LoLang.EN),
              text: MvnLanguageText.EN,
              type: "item",
            },
            {
              onClick: () => handleChangeLocaleLanguage(LoLang.KO),
              text: MvnLanguageText.KO,
              type: "item",
            },
          ]}
          onClose={() => setLanguageEl(null)}
        />
      </HeaderItemsWrapper>
    </HeaderWrapper>
  );
}

const HeaderWrapper = styled("header")<{ sidebarCollapsed: UseBoolean; isHiddenSidebar: UseBoolean }>(
  ({ sidebarCollapsed, isHiddenSidebar }) => ({
    position: "fixed",
    top: 0,
    right: 0,
    left: 0,
    height: baseHeight.appHeaderHeight,
    zIndex: baseZIndex.layout.header,
    minWidth: baseWidth.appHeaderMinWidth,
    background: palette["maven-blue-bright"],
    color: palette.white,
    transition: "left ease-in-out 0.23s",
    display: "flex",
    alignItems: "center",

    padding: "0px 24px 0px 32px",

    ...Object.assign({}, typography["Content.Body04"] || {}),

    "@media (max-width: 1024px)": {
      left: !isHiddenSidebar ? baseWidth.appSidebarWidthCollapsed : 0,
    },

    [baseMedia.mobile]: {
      minWidth: baseWidth.appHeaderMinWidthMobile,
    },

    ...(sidebarCollapsed.value && {}),
  })
);

const HeaderItemsWrapper = styled(GrStack)`
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  height: 100%;
`;

const HeaderItemsNavigator = styled(GrStack)`
  float: right;
  align-items: center;

  & > div {
    display: flex;
    align-items: center;
  }
`;

const Divider = styled(GrDivider)`
  width: 1px;
  height: 20px;
  margin: 0px 8px !important;
  border-color: transparent !important;
  background-color: ${palette["blue040"]};
`;

const TextButton = styled(GrButton)`
  &:hover {
    background-color: transparent;
  }

  &:active {
    background-color: transparent;
  }
`;

const QuestionMarkIconButton = styled(GrIconButton)`
  &:hover {
    background-color: ${palette["maven-blue-bright"]};
  }

  &:active {
    background-color: transparent;
  }
`;
